// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

import { type FC, useEffect, useRef, useCallback } from 'react';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import { ChatInput } from './ChatInput';
import { ChatMessageComponent } from './ChatMessage';
import { logger } from '../../lib/logger';
import { useChatStore } from '../../store/chatStore';
import { useGameStore } from '../../store/gameStore';
import { MessageType } from '../../types/chat';
import { ClientActionEnum } from '../../types/game';

interface ChatPanelProps {
  sendMessage: (action: ClientActionEnum, payload?: Record<string, unknown>) => void;
}

export const ChatPanel: FC<ChatPanelProps> = ({ sendMessage }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<HTMLTextAreaElement>(null);
  const deliveryTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const [messagesParent] = useAutoAnimate();
  const {
    messages,
    typingPlayers,
    isOpen,
    unreadCount,
    soundEnabled,
    toggleChat,
    clearChat,
    toggleSound,
    addPendingMessage,
    markDeliveryFailed,
  } = useChatStore();
  const { playerId, sessionId, displayName } = useGameStore();

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Request chat history when panel opens
  useEffect(() => {
    if (isOpen && sessionId) {
      logger.debug('requesting_chat_history');
      sendMessage(ClientActionEnum.CHAT_GET_HISTORY, { limit: 50 });
    }
  }, [isOpen, sessionId, sendMessage]);

  // Auto-focus chat input when panel opens
  useEffect(() => {
    if (isOpen && chatInputRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        chatInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Clear chat and delivery timers when session changes
  useEffect(() => {
    const timers = deliveryTimersRef.current;
    return () => {
      clearChat();
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, [sessionId, clearChat]);

  const handleSendMessage = useCallback(
    (content: string) => {
      if (!content.trim() || !playerId || !sessionId) return;

      // Generate temporary message ID for tracking
      const tempMessageId = `temp_${Date.now()}_${Math.random().toString(36).substring(7)}`;

      // Get current player name
      // Use displayName from store
      const playerName = displayName || 'You';

      // Create optimistic message
      const pendingMessage = {
        message_id: tempMessageId,
        session_id: sessionId,
        player_id: playerId,
        player_name: playerName,
        content: content.trim(),
        message_type: MessageType.PUBLIC,
        timestamp: new Date().toISOString(),
        edited: false,
        reactions: {},
      };

      // Add pending message optimistically
      addPendingMessage(pendingMessage);
      logger.debug('sending_chat_message', { content, tempMessageId });

      // Send to server with temp ID for correlation
      sendMessage(ClientActionEnum.CHAT_SEND_MESSAGE, {
        content: content.trim(),
        message_type: 'public',
        temp_message_id: tempMessageId,
      });

      // Set timeout to mark as failed if no confirmation received
      const deliveryTimer = setTimeout(() => {
        deliveryTimersRef.current.delete(deliveryTimer);
        const store = useChatStore.getState();
        if (store.pendingMessageIds.has(tempMessageId)) {
          logger.warn('message_delivery_timeout', { tempMessageId });
          markDeliveryFailed(tempMessageId);
        }
      }, 10000); // 10 second timeout
      deliveryTimersRef.current.add(deliveryTimer);
    },
    [sendMessage, playerId, sessionId, displayName, addPendingMessage, markDeliveryFailed]
  );

  const handleTypingStart = useCallback(() => {
    sendMessage(ClientActionEnum.CHAT_TYPING_START, {});
  }, [sendMessage]);

  const handleTypingStop = useCallback(() => {
    sendMessage(ClientActionEnum.CHAT_TYPING_STOP, {});
  }, [sendMessage]);

  const handleAddReaction = useCallback(
    (messageId: string, emoji: string) => {
      logger.debug('adding_reaction', { messageId, emoji });
      sendMessage(ClientActionEnum.CHAT_ADD_REACTION, {
        message_id: messageId,
        emoji,
      });
    },
    [sendMessage]
  );

  const handleRemoveReaction = useCallback(
    (messageId: string, emoji: string) => {
      logger.debug('removing_reaction', { messageId, emoji });
      sendMessage(ClientActionEnum.CHAT_REMOVE_REACTION, {
        message_id: messageId,
        emoji,
      });
    },
    [sendMessage]
  );

  // Filter out current player from typing list
  const otherTypingPlayers = typingPlayers.filter(id => id !== playerId);

  if (!isOpen) {
    // Chat toggle button (minimized state)
    return (
      <button
        onClick={toggleChat}
        className="fixed bottom-4 right-4 bg-blue-500 text-white p-3 sm:p-4 rounded-full shadow-lg hover:bg-blue-600 transition-colors z-50 min-h-12 min-w-12 sm:min-h-14 sm:min-w-14 flex items-center justify-center"
        aria-label="Open chat"
        data-testid="chat-toggle-button"
      >
        <svg
          className="w-5 h-5 sm:w-6 sm:h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full min-h-6 min-w-6 flex items-center justify-center font-semibold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 md:bottom-4 md:right-4 md:inset-auto w-full md:w-96 h-screen md:h-[500px] bg-white dark:bg-gray-800 rounded-none md:rounded-lg shadow-xl flex flex-col z-50 border-t md:border border-gray-200 dark:border-gray-700"
      data-testid="chat-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 md:p-3 border-b border-gray-200 dark:border-gray-700 bg-blue-500 text-white rounded-none md:rounded-t-lg">
        <h3 className="font-semibold">Chat</h3>
        <div className="flex items-center gap-1">
          <button
            onClick={toggleSound}
            className="hover:bg-blue-600 p-2 md:p-1 rounded transition-colors min-h-10 min-w-10 md:min-h-8 md:min-w-8 flex items-center justify-center"
            aria-label={soundEnabled ? 'Mute notifications' : 'Unmute notifications'}
            title={soundEnabled ? 'Mute notifications' : 'Unmute notifications'}
          >
            {soundEnabled ? (
              <svg
                className="w-5 h-5 md:w-4 md:h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 md:w-4 md:h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2"
                />
              </svg>
            )}
          </button>
          <button
            onClick={toggleChat}
            className="hover:bg-blue-600 p-2 md:p-1 rounded transition-colors min-h-10 min-w-10 md:min-h-8 md:min-w-8 flex items-center justify-center"
            aria-label="Close chat"
            data-testid="chat-close-button"
          >
            <svg
              className="w-5 h-5 md:w-4 md:h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages */}
      <div ref={messagesParent} className="flex-1 overflow-y-auto p-3 space-y-1">
        {messages.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          <>
            {messages.map(msg => (
              <div key={msg.message_id} className="group">
                <ChatMessageComponent
                  message={msg}
                  isOwnMessage={msg.player_id === playerId}
                  onAddReaction={handleAddReaction}
                  onRemoveReaction={handleRemoveReaction}
                  currentPlayerId={playerId || ''}
                />
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <ChatInput
        ref={chatInputRef}
        onSendMessage={handleSendMessage}
        onTypingStart={handleTypingStart}
        onTypingStop={handleTypingStop}
        typingPlayers={otherTypingPlayers}
        disabled={!sessionId}
      />
    </div>
  );
};
